import { useEffect } from 'react';
import { applyWallArtFacing } from './render/wallArtFacing';
import { useQueryClient } from '@tanstack/react-query';
import * as PIXI from 'pixi.js';
import posthog from 'posthog-js';
import { getFurnitureImagePath } from '../../data/furniture';
import {
  isRugCategory,
  isWallMountedItem,
  getTileDims,
} from '../../domain/tileMath';
import { stageLayout } from './state/layoutSync';
import { OPAQUE_PIXEL_ALPHA_THRESHOLD, applyAlphaMaskToSprite, type AlphaMask } from './render/alphaMask';
import { asFurnitureSprite, type FurnitureSprite } from './render/pixiExtensions';
import {
  getFurnitureDepthComponents,
  getFurnitureDepth,
  getOccupiedBaseTile,
  recomputeAllZIndexes,
} from '../../domain/depthMath';
import { isPlacedWallItemValid } from '../../domain/windowPlacement';
import { refreshPlacementTints } from '../../domain/placementTints';
import { redrawOccupiedTilesOverlay } from './render/occupiedOverlay';
import { smoothWhenMinified } from './render/textureFilter';
import { ROOM_ART_SCALE } from './render/textures';
import type { RenderFurniture } from '../../domain/types';
import type { RoomSceneState } from './state/roomSceneState';
import { attachInstrumentTap, stopInstrumentMelody } from './interaction/instrumentMelody';
import { WINDOW_VERTICAL_OFFSET } from '../../domain/windowSnap';

// ── Furniture render effect: async chunked render pass for placed furniture ──
// Extracted verbatim from RoomCanvas (decomposition step: furniture-render effect).
// The effect body is byte-identical to the original except that:
//  * RoomCanvas-owned refs, state flags and callbacks are received as parameters;
//  * the module-level `interiorContainer` / `rugContainer` scene state is read
//    through `getInteriorContainer()` / `getRugContainer()` getters, shadowed as
//    locals at the top of the effect (same read-at-invocation-time semantics);
//  * TILE_WIDTH / TILE_HEIGHT are still used by other RoomCanvas code (cobweb
//    long-press tile math), so they are passed in as parameters;
//  * setupFurnitureInteraction is declared later in RoomCanvas (TDZ), so the
//    call site passes an arrow-wrapper — same precedent as executeRadialAction.
// Constants used ONLY by this effect moved here with it.

const DEBUG_HIDE_FURNITURE_SPRITES = false;
/** A just-bought item is drawn above everything until the player touches the room. */
const BOUGHT_ITEM_ZINDEX = 99999;

/** Show a hidden room in a short fade (the ticker-free way: a few rAF steps). */
function fadeIn(container: PIXI.Container, ms = 180): void {
  const start = performance.now();
  const step = () => {
    if (container.destroyed) return;
    const t = Math.min(1, (performance.now() - start) / ms);
    container.alpha = t;
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

export interface UseFurnitureRenderParams {
  mapObjectId: number;
  placedFurniture: RenderFurniture[];
  flags: FurnitureRenderFlags;
  tileSize: FurnitureRenderTileSize;
  scene: FurnitureRenderScene;
  sceneRef: React.MutableRefObject<RoomSceneState>;
  alphaMasks: FurnitureRenderAlphaMasks;
  callbacks: FurnitureRenderCallbacks;
}

export interface FurnitureRenderFlags {
  canManageFurniture: boolean;
  isCityHall: boolean;
  isSceneReady: boolean;
  effectiveIsOwner: boolean | undefined;
  tileGridDebugEnabled: boolean;
}

export interface FurnitureRenderTileSize {
  TILE_WIDTH: number;
  TILE_HEIGHT: number;
}

export interface FurnitureRenderScene {
  getInteriorContainer: () => PIXI.Container | null;
  getRugContainer: () => PIXI.Container | null;
}

/** Access scene via sceneRef.current.* (mutable PIXI bag). */
export type FurnitureRenderRefs = React.MutableRefObject<RoomSceneState>;

export interface FurnitureRenderAlphaMasks {
  ensureAlphaMaskLoaded: (imagePath: string) => Promise<AlphaMask | null>;
  peekAlphaMask: (imagePath: string) => AlphaMask | null | undefined;
}

export interface FurnitureRenderCallbacks {
  setCityHallFurnitureInfo: (furniture: RenderFurniture | null) => void;
  setupFurnitureInteraction: (sprite: PIXI.Sprite, furniture: RenderFurniture, markerContainer: PIXI.Container) => void;
  showGreenOutline: (marker: PIXI.Container, strong?: boolean) => void;
  /** Bring the camera to a freshly added item so even a tiny one is clearly visible. */
  focusNewItem: (marker: PIXI.Container) => void;
  logFurnitureDiagnostics: () => void;
}

export function useFurnitureRender({
  mapObjectId,
  placedFurniture,
  flags: {
    canManageFurniture,
    isCityHall,
    isSceneReady,
    effectiveIsOwner,
    tileGridDebugEnabled,
  },
  tileSize: {
    TILE_WIDTH,
    TILE_HEIGHT,
  },
  scene: {
    getInteriorContainer,
    getRugContainer,
  },
  sceneRef,
  alphaMasks: {
    ensureAlphaMaskLoaded,
    peekAlphaMask,
  },
  callbacks: {
    setCityHallFurnitureInfo,
    setupFurnitureInteraction,
    showGreenOutline,
    focusNewItem,
    logFurnitureDiagnostics,
  },
}: UseFurnitureRenderParams): void {
  const queryClient = useQueryClient();
  // Render furniture when scene is ready or data changes
  // Leaving the room silences any instrument that is still playing.
  useEffect(() => () => stopInstrumentMelody(), []);

  useEffect(() => {
    // Module-level mutable scene state (RoomCanvas), read through getters at
    // effect run time and shadowed as locals — same values the original read.
    const interiorContainer = getInteriorContainer();
    const rugContainer = getRugContainer();
    if (!isSceneReady || !interiorContainer || !rugContainer) return;
    const currentPass = ++sceneRef.current.furniture.renderPass;

    const renderObjects = async () => {
      const furnitureContainer = interiorContainer!;
      const rugCont = rugContainer!;
      const isStalePass = () => currentPass !== sceneRef.current.furniture.renderPass;
      
      // Get currently dragging furniture ID to avoid re-rendering it
      const draggingFurnitureId = sceneRef.current.drag.state?.interiorId ?? null;
      
      // Remove markers that are no longer in placedFurniture (except if currently dragging)
      const furnitureIds = new Set(placedFurniture.map(f => f.id));
      for (const [furnitureId, marker] of sceneRef.current.furniture.markers.entries()) {
        if (!furnitureIds.has(furnitureId) && furnitureId !== draggingFurnitureId) {
          // Remove from whichever container the marker is in (rug or furniture)
          marker.parent?.removeChild(marker);
          marker.destroy({ children: true });
          sceneRef.current.furniture.markers.delete(furnitureId);
        }
      }

      // ── The room appears whole ──
      // Entering a room used to show the walls and then the furniture piece by
      // piece. The first pass now builds everything with the room hidden and
      // shows it at once; later passes (edits, purchases) draw in place.
      const room = furnitureContainer.parent;
      const firstShow = !sceneRef.current.furniture.revealed;
      if (firstShow && room) room.alpha = 0;
      // Every picture and its hit mask requested together (the splash may have
      // preloaded them — mapPreload.ts); the loop below then finds them ready.
      if (firstShow) {
        await Promise.all(placedFurniture.map((f) => {
          if (f.x === null || f.y === null) return null;
          const path = getFurnitureImagePath(f.furniture_catalog);
          return PIXI.Assets.load(path).then(() => ensureAlphaMaskLoaded(path)).catch(() => null);
        }));
        if (isStalePass()) return;
      }

      // ── Chunk + yield (mirror MapCanvas.createAsyncMarkers) ──
      // Process furniture in small batches and yield a frame between them so a
      // furniture-heavy room paints progressively instead of freezing the main
      // thread in one long synchronous block. Order is preserved, so z-order /
      // footprint / surface / rug logic is unaffected (recomputeAllZIndexes runs
      // once at the end as before).
      const RENDER_CHUNK = 20; // create this many sprites, then yield a frame
      const yieldFrame = () => new Promise<void>(r => requestAnimationFrame(() => r()));
      let createdThisFrame = 0;

      for (const furniture of placedFurniture) {
        if (isStalePass()) return;
        if (furniture.x === null || furniture.y === null) continue;
        const mirrorTrace = sceneRef.current.mirrorTrace.map.get(furniture.id) ?? null;
        
        // Skip if furniture_catalog is missing (API might not include it)
        if (!furniture.furniture_catalog) {
          // console.warn(`[RoomCanvas] Furniture ${furniture.id} missing furniture_catalog. Full furniture data:`, furniture);
          continue;
        }

        // Walls/floors are room decorations — they swap textures, not physical sprites
        if (furniture.furniture_catalog.category === 'walls' || furniture.furniture_catalog.category === 'floors') {
          continue;
        }

        // Skip re-rendering if this furniture is currently being dragged or carried
        if (furniture.id === draggingFurnitureId) {
          continue;
        }
        if (sceneRef.current.drag.draggingCarriedIds.has(furniture.id)) {
          continue;
        }

        // Check if marker already exists - update position instead of recreating
        const existingMarker = sceneRef.current.furniture.markers.get(furniture.id);
        if (existingMarker && existingMarker.position) {
          // Update position if changed (apply vertical offset for windows/wall-mounted)
          const isWindowItem = isWallMountedItem(furniture.furniture_catalog);
          const wallFloorOff = isWindowItem ? (furniture.wall_floor_offset ?? furniture.furniture_catalog?.wall_floor_offset ?? 0) : 0;
          const expectedY = furniture.y !== null && furniture.y !== undefined
            ? furniture.y + (isWindowItem ? WINDOW_VERTICAL_OFFSET : 0) + wallFloorOff
            : furniture.y;
          
          // The layout cache is the truth (local-first: a drop writes it before the
          // backend hears of it, and the dragged item is skipped above), so wall items
          // follow it like everything else — that is also how undo/redo moves them.
          const shouldUpdatePosition = existingMarker.position.x !== furniture.x || existingMarker.position.y !== expectedY;
          if (shouldUpdatePosition) {
            existingMarker.position.set(furniture.x, expectedY);
          }
          // Recompute depth every pass (footprint can change when catalog data arrives/refines)
          {
            const { w, h, isRug: itemIsRug } = getTileDims(furniture.furniture_catalog);
            const targetDepth = getFurnitureDepth(existingMarker, w, h, itemIsRug);
            if (existingMarker.zIndex !== targetDepth) {
              existingMarker.zIndex = targetDepth;
            }
          }
          // Update mirror state if changed
          const sprite = asFurnitureSprite(existingMarker.children[0] as PIXI.Sprite);
          if (sprite) {
            // CRITICAL: always keep furnitureData in sync with the latest query data.
            // redrawOccupiedTilesOverlay reads is_mirrored from furnitureData, so stale
            // data means the overlay ignores mirror changes.
            sprite.furnitureData = furniture;
            sprite.cursor = canManageFurniture ? 'grab' : 'default';
            sprite.alpha = DEBUG_HIDE_FURNITURE_SPRITES ? 0 : 1;
            const shouldBeMirrored = furniture.is_mirrored;
            const isMirrored = sprite.scale.x < 0;
            if (shouldBeMirrored !== isMirrored) {
              const { w: fW, h: fH, isRug: fIsRug } = getTileDims(furniture.furniture_catalog);
              sprite.scale.x = shouldBeMirrored ? -Math.abs(sprite.scale.x) : Math.abs(sprite.scale.x);
              applyWallArtFacing(sprite);
              // __visualBaseOffsetX stores the SIGNED non-mirrored visual offset.
              // Non-mirrored: sprite.x = base   |   Mirrored: sprite.x = -base
              const storedBase = sprite.__visualBaseOffsetX;
              const nonMirroredOffsetX = typeof storedBase === 'number'
                ? storedBase
                : (sprite.x ?? 0); // fallback: current value is assumed non-mirrored
              sprite.__visualBaseOffsetX = nonMirroredOffsetX;
              const newOffsetX = shouldBeMirrored ? -nonMirroredOffsetX : nonMirroredOffsetX;
              sprite.x = newOffsetX;
              sprite.__visualOffsetX = newOffsetX;
              const afterDepth = getFurnitureDepthComponents(existingMarker, fW, fH, fIsRug);
              const afterBase = getOccupiedBaseTile(afterDepth, shouldBeMirrored);
              // The swapped (post-mirror) footprint may now stick out of the room (e.g.
              // into a wall). Shift the item back inside by exactly the clamped amount —
              // a free (sub-tile) position stays free; an in-bounds item does not move.
              const shiftTX = afterBase.baseTileX - afterBase.rawBaseTileX;
              const shiftTY = afterBase.baseTileY - afterBase.rawBaseTileY;
              if ((shiftTX !== 0 || shiftTY !== 0) && furniture.x != null && furniture.y != null) {
                const floorX = furniture.x + (shiftTX - shiftTY) * (TILE_WIDTH / 2);
                const floorY = furniture.y + (shiftTX + shiftTY) * (TILE_HEIGHT / 2);
                const isWindowItem = isWallMountedItem(furniture.furniture_catalog);
                const markerY = floorY + (isWindowItem ? WINDOW_VERTICAL_OFFSET : 0) + (isWindowItem ? (furniture.wall_floor_offset ?? furniture.furniture_catalog?.wall_floor_offset ?? 0) : 0);
                existingMarker.position.set(floorX, markerY);
                existingMarker.zIndex = getFurnitureDepth(existingMarker, fW, fH, fIsRug);
                // Persist the corrected floor position (backend stores it without offsets).
                stageLayout(queryClient, mapObjectId, [{ id: furniture.id, x: floorX, y: floorY }]);
              }
            }

            // If marker was created while canManageFurniture=false (e.g. during userData refetch),
            // attach interactions once access becomes available.
            if (canManageFurniture && !sprite.__interactionSetup) {
              setupFurnitureInteraction(sprite, furniture, existingMarker);
              sprite.__interactionSetup = true;
            }
          }
          if (mirrorTrace) {
            sceneRef.current.mirrorTrace.map.delete(furniture.id);
          }
          continue;
        }

        {
          const imagePath = getFurnitureImagePath(furniture.furniture_catalog);
          const texture = await PIXI.Assets.load(imagePath);
          sceneRef.current.backdrop.loadedTexturePaths.add(imagePath);
          if (isStalePass()) return;
          
          // Check if texture loaded successfully
          if (!texture) {
            // console.error(`[RoomCanvas] Failed to load texture for furniture ${furniture.id}, imagePath: ${imagePath}`);
            continue;
          }

          // Another render pass may have already created this marker while texture was loading.
          if (sceneRef.current.furniture.markers.has(furniture.id)) {
            continue;
          }

          // Furniture art is shipped at ROOM_ART_SCALE x the in-game size, so it is
          // mostly minified — it needs mipmaps and averaging inside the level, or
          // it boils while the camera pans.
          smoothWhenMinified(texture.source);

          const markerContainer = new PIXI.Container();
          const sprite = new PIXI.Sprite(texture) as FurnitureSprite;
          sprite.anchor.set(0.5, 1.0); // bottom-center anchor
          
          // In-game size = picture size / ROOM_ART_SCALE (width and height separately)
          sprite.width = texture.width / ROOM_ART_SCALE;
          sprite.height = texture.height / ROOM_ART_SCALE;

          // Apply mirror
          if (furniture.is_mirrored) {
            sprite.scale.x = -Math.abs(sprite.scale.x);
          }
          sprite.alpha = DEBUG_HIDE_FURNITURE_SPRITES ? 0 : 1;

          markerContainer.addChild(sprite);

          // ── Pixel-perfect hit area + floor-contact offsets (DEFERRED) ──
          // The mask scan (decode + getImageData + pixel walk) is the expensive part.
          // We split it from sprite creation: the visual sprite is placed now; the
          // precise mask is applied either synchronously (if its already cached — the
          // re-entry / duplicate-furniture path) or lazily after first paint.
          //
          // IMPORTANT: for rugs and wall-mounted/windows the depth/zIndex math reads
          // the mask-derived offsets (__visualBaseOffsetX / __maxOpaqueWidthPx), so we
          // MUST resolve their mask BEFORE depth is computed — otherwise z-order would
          // change. Regular furniture's depth never uses these offsets, so it defers.
          const textureWidth = texture.orig.width;
          const textureHeight = texture.orig.height;
          const isWindowItem = isWallMountedItem(furniture.furniture_catalog);
          const itemIsRugCat = isRugCategory(furniture.furniture_catalog?.category);
          const depthDependsOnMask = itemIsRugCat || isWindowItem;

          // Neutral defaults so any consumer reading offsets before the mask is
          // applied gets sane values (these match the "no opaque pixels" branch).
          sprite.__alphaMask = null;
          sprite.__visualBaseOffsetX = 0;
          sprite.__visualOffsetX = 0;
          sprite.__visualBottomOffset = 0;
          sprite.__maxOpaqueWidthPx = 0;

          // hitArea reads __alphaMask DYNAMICALLY: bbox fallback until the mask lands,
          // then automatically pixel-perfect — no need to recreate this closure.
          sprite.hitArea = {
            contains: (x: number, y: number) => {
              // Local bounds for anchor (0.5, 1.0)
              if (x < -textureWidth / 2 || x > textureWidth / 2 || y < -textureHeight || y > 0) {
                return false;
              }

              // If mask is not available yet, fall back to sprite-bounds interaction
              // (the whole bbox is hittable). Tapping still selects the furniture.
              const mask = sprite.__alphaMask;
              if (!mask) return true;

              const u = (x + textureWidth / 2) / textureWidth;
              const v = (y + textureHeight) / textureHeight;
              if (u < 0 || u > 1 || v < 0 || v > 1) return false;

              const pixelX = Math.min(mask.width - 1, Math.max(0, Math.floor(u * (mask.width - 1))));
              const pixelY = Math.min(mask.height - 1, Math.max(0, Math.floor(v * (mask.height - 1))));
              const alpha = mask.data[pixelY * mask.width + pixelX];
              return alpha > OPAQUE_PIXEL_ALPHA_THRESHOLD;
            },
          };

          // Resolve the mask now if it's cheap (already cached) or required for depth
          // (rugs/windows). Otherwise defer to the idle drain after first paint.
          const cachedMask = peekAlphaMask(imagePath);
          if (depthDependsOnMask) {
            const maskForDepth = cachedMask !== undefined ? cachedMask : await ensureAlphaMaskLoaded(imagePath);
            if (isStalePass()) return;
            applyAlphaMaskToSprite(sprite, maskForDepth ?? null, isWindowItem, !!furniture.is_mirrored);
          } else if (cachedMask !== undefined) {
            // Already decoded (re-entry / duplicate item) — apply instantly, no settle.
            applyAlphaMaskToSprite(sprite, cachedMask, isWindowItem, !!furniture.is_mirrored);
          } else {
            // The mask scan ALSO produces the sprite's visual offset (sprite.x/y: horizontal
            // centering on the opaque support + floor-contact). Those are VISIBLE, so they must
            // be applied before paint — deferring them made furniture spawn shifted then snap.
            // Resolve synchronously (the expensive getImageData decode is needed for the offset
            // anyway, so there's nothing useful to defer). The chunked rAF yield still keeps the
            // room painting progressively.
            const resolvedMask = await ensureAlphaMaskLoaded(imagePath);
            if (isStalePass()) return;
            applyAlphaMaskToSprite(sprite, resolvedMask ?? null, isWindowItem, !!furniture.is_mirrored);
          }
          // Apply vertical offset for windows (lift them above the floor + catalog-level wall_floor_offset)
          const windowY = furniture.y !== null && furniture.y !== undefined
            ? furniture.y + (isWindowItem ? WINDOW_VERTICAL_OFFSET : 0) + (isWindowItem ? (furniture.wall_floor_offset ?? furniture.furniture_catalog?.wall_floor_offset ?? 0) : 0)
            : furniture.y;
          markerContainer.position.set(furniture.x, windowY);
          {
            const { w, h, isRug: itemIsRug } = getTileDims(furniture.furniture_catalog);
            markerContainer.zIndex = getFurnitureDepth(markerContainer, w, h, itemIsRug);
          }

          // ── Window/wall-mounted initial visual feedback ──
          // Red when it is not on a valid wall spot, so the user knows to move it.
          if (isWallMountedItem(furniture.furniture_catalog)
            && !isPlacedWallItemValid(sceneRef.current.furniture.markers, markerContainer, furniture, sprite)) {
            sprite.tint = 0xFF4444;
            sprite.alpha = 0.5;
          }

          // Container should not be interactive itself, but must allow interactive children.
          markerContainer.eventMode = 'passive';
          
          // Sprite handles all interactions - only clicks on the actual image
          sprite.eventMode = 'static';
          sprite.cursor = canManageFurniture ? 'grab' : 'default';
          sprite.furnitureData = furniture;
          sprite.markerContainer = markerContainer;
          applyWallArtFacing(sprite); // paintings on the left wall: sheared, not mirrored

          if (canManageFurniture) {
            setupFurnitureInteraction(sprite, furniture, markerContainer);
            sprite.__interactionSetup = true;
            // City hall: also show info popup on simple tap (long press still opens radial menu)
            if (isCityHall) {
              let pointerDownTime = 0;
              let pointerDownPos = { x: 0, y: 0 };
              sprite.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
                pointerDownTime = Date.now();
                pointerDownPos = { x: e.clientX, y: e.clientY };
              });
              sprite.on('pointerup', (e: PIXI.FederatedPointerEvent) => {
                const elapsed = Date.now() - pointerDownTime;
                const dx = e.clientX - pointerDownPos.x;
                const dy = e.clientY - pointerDownPos.y;
                if (elapsed < 300 && dx * dx + dy * dy < 100) {
                  setCityHallFurnitureInfo(sprite.furnitureData ?? furniture);
                }
              });
            }
          } else {
            sprite.__interactionSetup = false;
            if (isCityHall) {
              sprite.cursor = 'pointer';
              sprite.on('pointerdown', () => {
                setCityHallFurnitureInfo(sprite.furnitureData ?? furniture);
              });
            }
          }

          // Pianos / organ play their melody on a tap — for every visitor.
          if (!furniture.is_preview) {
            attachInstrumentTap(sprite, furniture.furniture_catalog_id, () => sceneRef.current.flags.buildMode);
          }

          if (isStalePass()) {
            markerContainer.destroy({ children: true });
            return;
          }

          // Rugs go into rugContainer (renders below all furniture), everything else into furnitureContainer
          const targetContainer = isRugCategory(furniture.furniture_catalog?.category) ? rugCont : furnitureContainer;
          targetContainer.addChild(markerContainer);
          sceneRef.current.furniture.markers.set(furniture.id, markerContainer);
          if (mirrorTrace) {
            sceneRef.current.mirrorTrace.map.delete(furniture.id);
          }

          // Yield a frame every RENDER_CHUNK newly-created sprites so the main thread
          // isn't blocked while a large room loads — sprites appear progressively.
          if (++createdThisFrame >= RENDER_CHUNK) {
            createdThisFrame = 0;
            await yieldFrame();
            if (isStalePass()) return;
          }
        }
      }

      redrawOccupiedTilesOverlay(sceneRef.current.furniture.markers, sceneRef.current.overlays.occupiedTiles, sceneRef.current.flags.showGridMode);
      // Re-derive the red tints before recomputing z-indexes: items red only because
      // of a now-removed item, or moved back by undo, must not keep a stale tint.
      refreshPlacementTints(sceneRef.current.furniture.markers, new Set([
        ...(draggingFurnitureId != null ? [draggingFurnitureId] : []),
        ...sceneRef.current.drag.draggingCarriedIds,
      ]));
      // After all furniture is rendered, recalculate z-indexes with surface awareness
      // so items placed on surfaces (e.g. on top of a wardrobe) render above the surface.
      recomputeAllZIndexes(sceneRef.current.furniture.markers, sceneRef.current.drag.state, sceneRef.current.drag.draggingCarriedIds, logFurnitureDiagnostics);
      // A just-bought item stays on top through later render passes (the purchase
      // sync refreshes the cache more than once) until the player touches anything
      // — every tap or drag replaces or clears highlight.marker.
      const bought = sceneRef.current.highlight.marker;
      if (bought && !bought.destroyed) bought.zIndex = BOUGHT_ITEM_ZINDEX;
      // Apply green outline + z-index boost for newly added (bought or test-driven) item.
      if (sceneRef.current.highlight.pendingCatalogId !== null) {
        const targetCatalogId = sceneRef.current.highlight.pendingCatalogId;
        // Find the NEWEST item with this catalog_id (highest id = just purchased)
        let targetMarker: PIXI.Container | null = null;
        let bestId = -1;
        for (const fData of placedFurniture) {
          if (fData.furniture_catalog_id === targetCatalogId) {
            const m = sceneRef.current.furniture.markers.get(fData.id);
            if (m && fData.id > bestId) {
              bestId = fData.id;
              targetMarker = m;
            }
          }
        }
        if (targetMarker) {
          showGreenOutline(targetMarker, true);
          sceneRef.current.highlight.marker = targetMarker;
          targetMarker.zIndex = BOUGHT_ITEM_ZINDEX;
          focusNewItem(targetMarker);
        }
        sceneRef.current.highlight.pendingCatalogId = null;
      }
      // console.log('[RoomCanvas] === INITIAL LOAD diagnostics ===');
      logFurnitureDiagnostics();

      if (firstShow && room && !room.destroyed) {
        sceneRef.current.furniture.revealed = true;
        fadeIn(room);
      }

      // PostHog: fire ONCE per interior entry, after ALL furniture textures for the initial
      // dataset have settled (the awaited render loop above is fully complete) and all sprites
      // have been added. Guarded so furniture edits/drags (later render passes) don't re-fire.
      if (!sceneRef.current.analytics.didTrackInteriorFullyLoaded) {
        sceneRef.current.analytics.didTrackInteriorFullyLoaded = true;
        try {
          posthog.capture('house_interior_fully_loaded', {
            map_object_id: mapObjectId,
            is_owner: !!effectiveIsOwner,
            is_city_hall: isCityHall,
            furniture_count: sceneRef.current.furniture.markers.size,
            load_duration_ms: Math.round(performance.now() - sceneRef.current.analytics.interiorEnterTs),
          });
        } catch { /* never block game */ }
      }

    };

    // Never leave the room hidden: if this pass fails, or a picture never arrives
    // on a bad connection, show what is there.
    const reveal = () => {
      const room = interiorContainer.parent;
      if (sceneRef.current.furniture.revealed || !room || room.destroyed) return;
      sceneRef.current.furniture.revealed = true;
      fadeIn(room);
    };
    const revealAnyway = window.setTimeout(reveal, 8000);
    renderObjects().catch((e) => {
      console.error('[RoomCanvas] furniture render failed', e);
      reveal();
    });
    return () => {
      // Invalidate current async render pass to prevent stale adds and visual duplicates.
      // .current у cleanup навмисно: потрібен live renderPass, не mount snapshot.
      /* eslint-disable react-hooks/exhaustive-deps -- cleanup needs live renderPass, not mount snapshot */
      if (sceneRef.current.furniture.renderPass === currentPass) {
        sceneRef.current.furniture.renderPass++;
      }
      window.clearTimeout(revealAnyway);
      /* eslint-enable react-hooks/exhaustive-deps */
    };
    // Тригер: placedFurniture + scene ready + flags. setupFurnitureInteraction /
    // showGreenOutline / get*Container / peekAlphaMask / TILE_* — нестабільні або
    // константи; додавання setupFurnitureInteraction = full furniture rebuild щорендер
    // (callback identity з useFurnitureInteraction). sceneRef/mapObjectId/effectiveIsOwner
    // читаються імперативно в body (analytics) — mapObjectId+effectiveIsOwner рідко
    // змінюються без remount scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- furniture data drives render pass
  }, [placedFurniture, canManageFurniture, isCityHall, setCityHallFurnitureInfo, isSceneReady, ensureAlphaMaskLoaded, tileGridDebugEnabled, logFurnitureDiagnostics, sceneRef]);
}
